import { ShieldCheck, Lock, Download } from 'lucide-react';
import { Reveal, Chapter } from './Reveal';
import { GoldButton } from './GoldButton';
import { CHECKOUT_LINKS, PRICING, APP_URL } from '../../config/site';
import { trackClick } from '../../lib/track';

const PriceBlock = ({ oldPrice, price, suffix }) => (
  <div className="mt-6">
    <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-gold">Preço de lançamento</span>
    <div className="mt-3 flex items-end gap-3">
      <span className="mb-1.5 text-sm text-neutral-500 line-through">{oldPrice}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="font-display text-5xl font-extrabold tracking-tight text-white">{price}</span>
      {suffix && <span className="text-base text-neutral-400">{suffix}</span>}
    </div>
  </div>
);

export const Pricing = () => (
  <section id="precos" data-testid="pricing-section" className="relative border-t border-white/5">
    <div className="pointer-events-none absolute left-1/2 top-1/3 -z-10 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-gold/[0.06] blur-[140px]" />
    <div className="mx-auto max-w-5xl px-6 py-24 sm:px-10 sm:py-36">
      <Chapter index="04" label="Ofertas" />

      <Reveal>
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Escolha como quer <span className="text-gold">começar.</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-2">
        <Reveal>
          <div data-testid="pricing-card-app" className="h-full rounded-3xl bg-gradient-to-b from-gold/50 via-gold/15 to-transparent p-[1px]">
            <div className="flex h-full flex-col rounded-[calc(1.5rem-1px)] bg-[#0b0b0b] p-8 sm:p-10">
              <span className="font-accent text-[11px] tracking-[0.3em] uppercase text-gold">Aplicativo · Gratuito</span>
              <h3 className="mt-3 font-display text-2xl font-bold text-white">Baixe o Despertar da Águia</h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                Sua jornada de 28 dias começa hoje — direto do navegador, sem custo e sem cadastro complicado.
              </p>
              <div className="mt-6 overflow-hidden rounded-2xl border border-gold/15 bg-black">
                <div className="relative flex flex-col items-center px-6 py-8">
                  <div className="absolute left-1/2 top-2.5 h-4 w-16 -translate-x-1/2 rounded-full bg-white/[0.06]" />
                  <img
                    src="/assets/app-icon.webp"
                    alt="Ícone do app Despertar da Águia"
                    loading="lazy"
                    className="mt-4 w-16 rounded-[26%] shadow-[0_10px_30px_rgba(0,0,0,0.7)]"
                  />
                  <p className="font-cinzel mt-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-white">
                    Despertar da Águia
                  </p>
                  <div className="bg-gold mt-5 flex h-16 w-16 animate-pulse items-center justify-center rounded-full shadow-[0_0_45px_rgba(212,175,55,0.45)]">
                    <Download className="h-7 w-7 text-black" strokeWidth={2.4} />
                  </div>
                  <div className="mt-5 h-1.5 w-full max-w-[210px] overflow-hidden rounded-full bg-white/10">
                    <div className="bg-gold h-full w-[45%] rounded-full" />
                  </div>
                  <p className="mt-3 text-xs font-medium text-neutral-300">Baixar gratuitamente</p>
                  <p className="mt-0.5 font-accent text-[10px] tracking-[0.2em] uppercase text-neutral-600">Instalando…</p>
                </div>
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {['Jornada completa de 28 dias', 'Todos os 9 recursos liberados', 'Novos conteúdos toda semana'].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-neutral-300">
                    <span className="h-1 w-1 rotate-45 bg-gold" />
                    {f}
                  </li>
                ))}
              </ul>
              <GoldButton
                testId="pricing-app-cta-button"
                href={APP_URL}
                onClick={() => trackClick('precos-baixar-gratuitamente', 'aplicativo')}
                className="mt-8 w-full"
              >
                Baixar gratuitamente
              </GoldButton>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div data-testid="pricing-card-training" className="h-full rounded-3xl border border-white/10 bg-graphite p-8 transition-colors duration-300 hover:border-white/20 sm:p-10">
            <div className="flex h-full flex-col">
              <span className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500">Pagamento único</span>
              <h3 className="mt-3 font-display text-2xl font-bold text-white">Treinamento Complementar</h3>
              <PriceBlock {...PRICING.training} />
              <p className="mt-4 text-sm text-neutral-400">Pagamento único. Acesso imediato e vitalício.</p>
              <ul className="mt-7 flex-1 space-y-3">
                {['7 módulos em vídeo', 'Assista no seu ritmo', 'Complemento ideal da jornada'].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-neutral-300">
                    <span className="h-1 w-1 rotate-45 bg-gold" />
                    {f}
                  </li>
                ))}
              </ul>
              <GoldButton
                testId="pricing-training-cta-button"
                href={CHECKOUT_LINKS.training}
                onClick={() => trackClick('precos-quero-meu-acesso', 'treinamento')}
                ghost
                className="mt-9 w-full"
              >
                Quero meu acesso
              </GoldButton>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-neutral-500">
          <span className="flex items-center gap-2 text-xs">
            <Lock className="h-3.5 w-3.5 text-gold/70" /> Pagamento seguro via Cakto
          </span>
          <span className="flex items-center gap-2 text-xs">
            <ShieldCheck className="h-3.5 w-3.5 text-gold/70" /> 7 dias de garantia incondicional
          </span>
        </div>
      </Reveal>
    </div>
  </section>
);
